import { ReactElement, useState, useEffect, useMemo, useCallback } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useForm } from '@tanstack/react-form'
import {
  Container,
  Box,
  Card,
  TextField,
  Skeleton,
  Typography,
} from '@mui/material'
import { GridRowSelectionModel } from '@mui/x-data-grid'

import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import {
  getZodValidationError,
  shouldShowZodFieldError,
} from 'src/v3/utilities/validation'
import { useAutoFetch } from 'src/v3/hooks/use-auto-fetch'
import {
  fetchGroups,
  fetchGroupsV2,
  fetchLightweightUsersV4,
  fetchLinkedServices,
  selectGroupById,
  selectLightweightUsersV4ByService,
  selectLightweightUsersV4StatusByService,
  selectLinkedServicesStatus,
  selectGroupsStatus,
} from '../manage.slice'
import UsersDataGrid from './components/users-data-grid'
import { GroupFormData, groupSchema } from './validation'
import { paperInputSx } from '../styles'
import {
  transformUsersToRows,
  filterUsers,
  extractLicenseTypes,
} from './utils/user-transforms'
import PageTitleWithLinkedService from '../components/page-title-with-linked-service'
import CreateFooterActionButtons from '../components/cancel-save-buttons'
import { updateGroup } from '../utils/api'
import { useNotifications } from '../hooks/use-notifications'
import { useLinkedServiceName } from '../hooks/use-linked-service-name'
import { GROUPS_URLS } from '../constants'

export default function GroupEditV4(): ReactElement {
  let navigate = useNavigate()
  let dispatch = useAppDispatch()
  let { groupID } = useParams<{ groupID: string }>()

  let { showError, NotificationComponent } = useNotifications()

  let [selectedUsers, setSelectedUsers] = useState<GridRowSelectionModel>({
    type: 'include',
    ids: new Set(),
  })
  let [initialUsers, setInitialUsers] = useState<GridRowSelectionModel>({
    type: 'include',
    ids: new Set(),
  })
  let [licenseTypeFilter, setLicenseTypeFilter] = useState('')
  let [searchQuery, setSearchQuery] = useState('')

  let group = useAppSelector(selectGroupById(groupID || ''))
  let fetchGroupsStatus = useAppSelector(selectGroupsStatus)
  let users = useAppSelector(
    selectLightweightUsersV4ByService(group?.linkedServiceID || ''),
  )
  let fetchUsersStatus = useAppSelector(
    selectLightweightUsersV4StatusByService(group?.linkedServiceID || ''),
  )
  let fetchLinkedServicesStatus = useAppSelector(selectLinkedServicesStatus)

  let userRows = useMemo(() => transformUsersToRows(users), [users])
  let availableLicenseTypes = useMemo(
    () => extractLicenseTypes(userRows),
    [userRows],
  )

  // Sort users to show initially selected users first (optimized with Set for O(1) lookup)
  let sortedUserRows = useMemo(() => {
    let initialSet = initialUsers.ids
    return userRows.toSorted((a, b) => {
      let aInitial = initialSet.has(a.id)
      let bInitial = initialSet.has(b.id)
      if (aInitial && !bInitial) return -1
      if (!aInitial && bInitial) return 1
      return 0
    })
  }, [userRows, initialUsers])

  // Filter users (always include selected users)
  let filteredUserRows = useMemo(
    () =>
      filterUsers(
        sortedUserRows,
        Array.from(selectedUsers.ids) as string[],
        licenseTypeFilter,
        searchQuery,
      ),
    [sortedUserRows, licenseTypeFilter, searchQuery, selectedUsers],
  )

  let linkedServiceName = useLinkedServiceName(group?.linkedServiceID)

  let form = useForm({
    defaultValues: {
      name: group?.name || '',
      userIDs: group?.users || [],
    } as GroupFormData,
    validators: {
      onChange: groupSchema,
    },
    onSubmit: async ({ value }) => {
      if (!group || !group.id) return

      let payload = {
        id: group.id,
        name: value.name,
        linkedServiceID: group.linkedServiceID,
        userIDs: value.userIDs,
      }

      await updateGroup(payload)
        .then(() => dispatch(fetchGroups()))
        .then(() => dispatch(fetchGroupsV2(group.linkedServiceID)))
        .then(() => {
          navigate(`${GROUPS_URLS.INDEX}/${groupID}`, {
            state: {
              successMessage: `Group "${group.name}" updated successfully.`,
            },
          })
        })
        .catch((err: any) => {
          let errorMessage = `Error updating group "${group.name}".`
          // eslint-disable-next-line no-console
          console.error(errorMessage, 'Payload: ', payload, 'Error', err)
          showError(errorMessage)
        })
    },
  })

  let handleSelectionChange = useCallback(
    (newSelection: GridRowSelectionModel) => {
      setSelectedUsers(newSelection)

      // Calculate selected IDs based on selection model type
      let selectedIDs =
        newSelection.type === 'exclude'
          ? (() => {
              // Select all: get all visible IDs except excluded ones
              let excludedIDs = newSelection.ids
                ? (Array.from(newSelection.ids) as string[])
                : []
              return filteredUserRows
                .map((row) => row.id)
                .filter((id) => !excludedIDs.includes(id))
            })()
          : newSelection.ids
            ? (Array.from(newSelection.ids) as string[])
            : []

      form.setFieldValue('userIDs', selectedIDs)
      form.validate('change')
    },
    [filteredUserRows, form],
  )

  let handleCancel = useCallback(() => {
    navigate(`${GROUPS_URLS.INDEX}/${groupID}`)
  }, [navigate, groupID])

  let handleResetFilters = useCallback(() => {
    setSearchQuery('')
    setLicenseTypeFilter('')
  }, [])

  let isLoading =
    fetchGroupsStatus === 'pending' ||
    fetchUsersStatus === 'pending' ||
    fetchLinkedServicesStatus === 'pending'

  useAutoFetch(fetchGroupsStatus, fetchGroups)
  useAutoFetch(fetchLinkedServicesStatus, fetchLinkedServices)

  useEffect(() => {
    if (group?.linkedServiceID && fetchUsersStatus === 'uninitialized') {
      dispatch(fetchLightweightUsersV4(group.linkedServiceID))
    }
  }, [group?.linkedServiceID, fetchUsersStatus, dispatch])

  useEffect(() => {
    if (group) {
      form.setFieldValue('name', group.name)
      form.setFieldValue('userIDs', group.users || [])
      let selectionModel: GridRowSelectionModel = {
        type: 'include',
        ids: new Set(group.users || []),
      }
      setSelectedUsers(selectionModel)
      setInitialUsers(selectionModel)
    }
  }, [group])

  return (
    <Container maxWidth="xl" className="py-6">
      <form
        onSubmit={(e) => {
          e.preventDefault()
          e.stopPropagation()
          form.handleSubmit()
        }}
        aria-label="Edit group form"
      >
        <Box className="mb-4">
          <PageTitleWithLinkedService
            pageTitle={group?.name ? `Edit ${group.name}` : 'Edit Group'}
            linkedServiceName={linkedServiceName ?? ''}
            moduleName="Groups"
            handleBackClick={() => navigate(`${GROUPS_URLS.INDEX}/${groupID}`)}
            isLoading={isLoading}
          />
        </Box>

        <Card className="p-6 mb-6">
          <Typography variant="h6" component="h2" className="mb-4">
            Group Information
          </Typography>
          <form.Field name="name">
            {(field) =>
              isLoading || !group ? (
                <Skeleton variant="rounded" height={56} />
              ) : (
                <TextField
                  fullWidth
                  required
                  label="Name"
                  placeholder="Enter group name..."
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                  error={shouldShowZodFieldError(field.state.meta)}
                  helperText={
                    shouldShowZodFieldError(field.state.meta)
                      ? getZodValidationError(field.state.meta.errors)
                      : undefined
                  }
                  aria-label="Group name"
                  aria-invalid={shouldShowZodFieldError(field.state.meta)}
                  sx={paperInputSx}
                />
              )
            }
          </form.Field>
        </Card>

        <form.Field name="userIDs">
          {(field) => (
            <UsersDataGrid
              users={filteredUserRows}
              selectedUsers={selectedUsers}
              onSelectionChange={handleSelectionChange}
              searchQuery={searchQuery}
              licenseTypeFilter={licenseTypeFilter}
              availableLicenseTypes={availableLicenseTypes}
              onSearchChange={setSearchQuery}
              onLicenseTypeChange={setLicenseTypeFilter}
              onResetFilters={handleResetFilters}
              isLoading={isLoading}
              field={field}
              editing={true}
              title="Users"
            />
          )}
        </form.Field>

        <form.Subscribe
          selector={(state) => [state.canSubmit, state.isSubmitting]}
        >
          {([canSubmit, isSubmitting]) => (
            <CreateFooterActionButtons
              moduleName="Group"
              canSubmit={canSubmit}
              isSubmitting={isSubmitting}
              isDisabled={isLoading || isSubmitting}
              handleSave={form.handleSubmit}
              handleCancel={handleCancel}
            />
          )}
        </form.Subscribe>
      </form>
      {NotificationComponent}
    </Container>
  )
}
